import React from "react";
import { motion } from "framer-motion";
import {
  Ticket,
  CreditCard,
  QrCode,
  RefreshCw,
  Tag,
  Users,
  ArrowRight,
  CheckCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { HomepageNavbar } from "../../../components/HomepageNavbar";
import { HomepageFooter } from "../../../components/HomepageFooter";
import { Helmet } from "react-helmet-async";

const TicketsPage: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Ticket,
      title: "Ticket creation",
      description:
        "Create multiple ticket types, tiers, and pricing structures for your events.",
    },
    {
      icon: CreditCard,
      title: "Local and international payments",
      description:
        "Accept payments from anywhere in the world with multiple currency support.",
    },
    {
      icon: QrCode,
      title: "QR check in",
      description:
        "Streamlined check in process using QR codes for fast and accurate attendance tracking.",
    },
    {
      icon: RefreshCw,
      title: "Refund rules",
      description:
        "Configure automatic refund policies and handle cancellations with ease.",
    },
    {
      icon: Tag,
      title: "Promo codes",
      description:
        "Create discount codes, early bird pricing, and special offers to boost sales.",
    },
    {
      icon: Users,
      title: "Attendance tracking",
      description:
        "Real time monitoring of ticket sales, check ins, and attendee status.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>
          Aiserwave Tickets - Event Ticketing Software | Online Event
          Registration
        </title>
        <meta
          name="description"
          content="Aiserwave Tickets manages registration, payments, ticket types, seat classes, and check in. It supports simple events and large multi day conferences."
        />
        <meta
          name="keywords"
          content="event ticketing software, online event registration, conference check in system, event ticket management"
        />
      </Helmet>
      <div className="min-h-screen bg-white">
        <HomepageNavbar />

        {/* Hero Section */}
        <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#f3f4ff] via-white to-white border-b border-gray-100">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#17265a]/5 text-[#17265a] text-xs font-semibold mb-4">
                <Ticket className="w-4 h-4" />
                Registration & Payments
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 mb-6">
                Aiserwave Tickets
              </h1>
              <p className="text-lg md:text-xl text-gray-600 max-w-3xl leading-relaxed">
                Aiserwave Tickets manages registration, payments, ticket types,
                seat classes, and check in. It supports simple events and large
                multi day conferences.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Overview Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Overview
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Aiserwave Tickets gives teams clarity and control at every
                stage. From ticket creation to check in, the module handles all
                aspects of event registration and payment processing with
                precision and reliability.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Key Features */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Key Features
              </h2>
            </motion.div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#17265a]/10 text-[#17265a] mb-4">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Value Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-[#17265a]/5 to-white border border-gray-200 rounded-2xl p-8 md:p-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Value
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Aiserwave Tickets gives teams clarity and control at every
                stage.
              </p>
              <div className="space-y-4">
                {[
                  "Streamlined registration process",
                  "Global payment acceptance",
                  "Real time sales tracking",
                  "Automated check in workflows",
                ].map((benefit) => (
                  <div key={benefit} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#17265a] flex-shrink-0" />
                    <span className="text-gray-700 font-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Ready to sell tickets?
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Start accepting registrations and payments with Aiserwave
                Tickets.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4 pt-4">
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate("/pricing")}
                  className="bg-[#17265a] text-white px-8 py-4 rounded-lg hover:bg-[#0f1a3f] transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl font-semibold text-lg group"
                >
                  <span>View Pricing</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate("/")}
                  className="bg-transparent border-2 border-[#17265a] text-[#17265a] px-8 py-4 rounded-lg hover:bg-[#17265a]/5 transition-all duration-300 flex items-center gap-2 font-semibold text-lg"
                >
                  <span>Back to Home</span>
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
              </div>
            </motion.div>
          </div>
        </section>

        <HomepageFooter />
      </div>
    </>
  );
};

export default TicketsPage;
